import React, { useState, useEffect, useRef } from 'react';
import './ChatView.css';
import { sendMessageToCustomer, setCustomerStatus, sendMenuToCustomer } from '../services/apiServices';

const ChatView = ({ customer, order, messages, isLoading, onClose, onAdminMessageSent, onConfirmPayment, onPushCart }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAdminInControl, setIsAdminInControl] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (customer.status === 'admin_chat_active') {
      setIsAdminInControl(true);
    } else {
      setIsAdminInControl(false);
    }
  }, [customer.id, customer.status]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // DEBUG: Adicionado para verificar as props recebidas
  useEffect(() => {
    if (messages && messages.length > 0) {
      console.log('[ChatView] Mensagens carregadas:', messages.length, 'Última:', messages[messages.length - 1]);
    }
  }, [customer, order, messages, isLoading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      // Envia a mensagem para o cliente via API
      await sendMessageToCustomer(customer.id, newMessage);
      
      // CHAMA A FUNÇÃO DO DASHBOARD: Atualiza a UI localmente em tempo real.
      if (onAdminMessageSent) {
        onAdminMessageSent(customer.id, newMessage);
      }
      setNewMessage('');
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      alert("Falha ao enviar mensagem.");
    }
  };

  const handleTakeOverChat = async () => {
    try {
      await setCustomerStatus(customer.id, 'admin_chat_active');
      setIsAdminInControl(true);
    } catch (error) {
      console.error("Erro ao assumir o controle do chat:", error);
    }
  };

  const handleEndChat = async () => {
    try {
      await sendMessageToCustomer(customer.id, 'Obrigado pelo contato! Nosso atendimento foi encerrado. Se precisar de algo mais, é só chamar. 👋');
      await setCustomerStatus(customer.id, 'idle');
      onClose();
    } catch (error) {
      console.error("Erro ao encerrar o chat:", error);
      alert("Falha ao encerrar o atendimento.");
    }
  };

  const handleConfirmPayment = async () => {
    if (!order || !order.id) return;
    if (onConfirmPayment) {
        onConfirmPayment(order.id);
    }
  };


  const handleSendMenu = async (menuType) => {
    try {
      await sendMenuToCustomer(customer.id, menuType);
      await setCustomerStatus(customer.id, 'awaiting_customer_action');
      onClose();
    } catch (error) {
      console.error(`Erro ao enviar menu de ${menuType}:`, error);
      alert(`Falha ao enviar o menu de ${menuType}.`);
    }
  };

  // Condição para mostrar o botão: o pedido existe, é Pix e não foi confirmado
  const showConfirmPaymentButton = order && order.paymentMethod && order.paymentMethod.toLowerCase().includes('pix') && !order.paymentConfirmed;

  return (
    <div className={`chat-view ${isMinimized ? 'minimized' : ''}`}>
      <div className="chat-header">
        <h3>Conversa com {customer.name}</h3>
        <div>
          <button onClick={() => setIsMinimized(!isMinimized)} className="minimize-btn">
            {isMinimized ? '❐' : '—'}
          </button>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>
      </div>
      {!isMinimized && (
        <>
          <div className="messages-container">
            {isLoading ? (
              <p className="log-bubble">Carregando mensagens...</p>
            ) : (
              <>
                {messages
                  .filter(msg => msg) // CORREÇÃO: Remove filtro estrito de timestamp para exibir mensagens sem data
                  .map((msg, index) => {
                    const currentMsgDate = msg.timestamp ? new Date(msg.timestamp).toLocaleDateString('pt-BR') : 'Data desconhecida';
                    const prevMsg = messages[index - 1]; // Pega a mensagem anterior
                    const prevMsgDate = prevMsg && prevMsg.timestamp ? new Date(prevMsg.timestamp).toLocaleDateString('pt-BR') : null;
                    const showDateDivider = currentMsgDate && currentMsgDate !== prevMsgDate;

                    const messageTime = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

                    return (
                      <React.Fragment key={index}>
                        {showDateDivider && <div className="date-divider">{currentMsgDate}</div>}
                        <div className={`message-bubble ${msg.role === 'model' ? 'admin' : 'user'}`}>
                          {msg.document ? (
                            <div style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', minWidth: '200px' }}>
                              <a href={msg.document.url || '#'} target="_blank" rel="noopener noreferrer" download={msg.document.filename} className="document-link" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: '#333', fontWeight: 'bold' }} onClick={(e) => !msg.document.url && e.preventDefault()}>
                                <span role="img" aria-label="Documento" style={{ fontSize: '24px' }}>📄</span> 
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                  <span style={{ wordBreak: 'break-all' }}>{msg.document.filename || 'Documento PDF'}</span>
                                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal' }}>Clique para baixar</span>
                                </div>
                              </a>
                              {msg.document.caption && <div style={{ marginTop: '5px', fontSize: '12px', color: '#333', borderTop: '1px solid #eee', paddingTop: '5px' }}>{msg.document.caption}</div>}
                            </div>
                          ) : msg.image ? (
                            <a href={msg.image.url || '#'} target="_blank" rel="noopener noreferrer" download={msg.image.caption || 'comprovante.jpg'} className="image-link" onClick={(e) => !msg.image.url && e.preventDefault()}>
                              <img src={msg.image.url || ''} alt={msg.image.caption || 'Comprovante'} className="chat-image" title="Clique para baixar a imagem" />
                              {msg.image.caption && <span className="caption">{msg.image.caption}</span>}
                            </a>
                          ) : msg.interactive?.button_reply?.title ? (
                            `Clicou em: "${msg.interactive.button_reply.title}"`
                          ) : msg.interactive?.list_reply?.title ? (
                            `Selecionou: "${msg.interactive.list_reply.title}"`
                          ) : (
                            msg.parts?.[0]?.text || msg.text?.body || (typeof msg === 'string' ? msg : 'Mensagem inválida')
                          )}
                          <div className="message-time">{messageTime}</div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                {/* Mensagem de fallback se não houver histórico */}
                {(!messages || messages.length === 0) && (
                  <p className="log-bubble">Nenhuma mensagem encontrada para este cliente.</p>
                )}
              </>
            )}

            <div ref={messagesEndRef} />
          </div>
          {isAdminInControl ? (
            <>
              <form className="message-form" onSubmit={handleSendMessage}>
                <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Digite sua mensagem..." />
                <button type="submit">Enviar</button>
              </form>
              <div className="actions-menu">
                <button onClick={() => handleSendMenu('categories')}>Enviar Cardápio</button>
                <button onClick={() => onPushCart(customer)}>Montar Carrinho</button>
                <button className="end-chat-btn" onClick={handleEndChat}>Encerrar Atendimento</button>
              </div>
            </>
          ) : (
            <div className="takeover-menu">
              <button className="takeover-btn" onClick={handleTakeOverChat}>Assumir Atendimento</button>
              {showConfirmPaymentButton && (
                <button className="confirm-payment-btn" onClick={handleConfirmPayment}>Confirmar Pagamento</button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatView;